import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ImageDebugger = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiUrl, setApiUrl] = useState(process.env.REACT_APP_API_URL || 'http://localhost:8000');
  
  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${apiUrl}/api/v1/tours`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      console.log('Tours API response:', response.data);
      
      if (response.data.success) {
        // Handle different response formats
        let toursData = [];
        if (Array.isArray(response.data.data)) {
          toursData = response.data.data;
        } else if (response.data.data && response.data.data.data) {
          toursData = response.data.data.data;
        }
        
        setTours(toursData);
      } else {
        setError('Failed to fetch tours');
      }
    } catch (error) {
      console.error('Error fetching tours:', error);
      setError('Error fetching tours: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Test different image URL construction methods
  const getImageUrlVariations = (tour) => {
    const variations = [];
    
    if (tour.featured_image) {
      // Variation 1: Direct path
      variations.push({
        name: 'Direct path',
        url: tour.featured_image,
      });
      
      // Variation 2: With storage prefix
      variations.push({
        name: 'With /storage/ prefix',
        url: `${apiUrl}/storage/${tour.featured_image}`,
      });
      
      // Variation 3: With storage/app/public prefix
      variations.push({
        name: 'With /storage/app/public/ prefix',
        url: `${apiUrl}/storage/app/public/${tour.featured_image}`,
      });
      
      // Variation 4: Without tours/featured prefix
      const pathWithoutPrefix = tour.featured_image.replace('tours/featured/', '');
      variations.push({
        name: 'Without tours/featured prefix',
        url: `${apiUrl}/storage/${pathWithoutPrefix}`,
      });
    }
    
    return variations;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Image Path Debugger</h1>
      
      {loading ? (
        <div className="text-center">
          <p>Loading tours...</p>
        </div>
      ) : error ? (
        <div className="bg-red-100 p-4 rounded">
          <p className="text-red-700">{error}</p>
        </div>
      ) : (
        <div>
          <p className="mb-4">Found {tours.length} tours to analyze</p>
          
          <div className="space-y-8">
            {tours.map(tour => (
              <div key={tour.id} className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-2">{tour.name}</h2>
                <p className="text-gray-600 mb-4">ID: {tour.id}</p>
                
                <div className="mb-4">
                  <h3 className="text-lg font-medium mb-2">Image Path Data:</h3>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-auto">
                    {JSON.stringify({
                      featured_image: tour.featured_image,
                      gallery_images: tour.gallery_images
                    }, null, 2)}
                  </pre>
                </div>
                
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-2">Testing Different URL Constructions:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getImageUrlVariations(tour).map((variation, index) => (
                      <div key={index} className="border rounded p-4">
                        <h4 className="font-medium mb-2">{variation.name}</h4>
                        <p className="text-xs text-gray-500 mb-2 break-all">{variation.url}</p>
                        <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded overflow-hidden">
                          <img 
                            src={variation.url} 
                            alt={`${tour.name} - ${variation.name}`}
                            className="object-cover w-full h-full"
                            onError={(e) => {
                              e.target.classList.add('border', 'border-red-500');
                              e.target.classList.remove('object-cover');
                              e.target.src = 'https://via.placeholder.com/400x225?text=Image+Load+Failed';
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-2">Working Solution:</h3>
                  <div className="border rounded p-4">
                    <p className="text-xs text-gray-500 mb-2 break-all">
                      {`${apiUrl}/storage/${tour.featured_image}`}
                    </p>
                    <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded overflow-hidden">
                      <img 
                        src={`${apiUrl}/storage/${tour.featured_image}`} 
                        alt={tour.name}
                        className="object-cover w-full h-full"
                        onError={(e) => {
                          e.target.classList.add('border', 'border-red-500');
                          e.target.classList.remove('object-cover');
                          e.target.src = 'https://via.placeholder.com/400x225?text=Image+Load+Failed';
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageDebugger;
